// Costs / cost-splitting API wrappers.
// player_id is always derived server-side from the session cookie.

import { uploadToCloudinary } from './api.js';

const TRIP_DEFAULT = 'juneau-2026';

async function jsonOrThrow(res) {
  let data;
  try {
    data = await res.json();
  } catch {
    data = {};
  }
  if (!res.ok) {
    const err = new Error(data.message || data.error || `${res.url} → ${res.status}`);
    err.status = res.status;
    err.detail = data;
    throw err;
  }
  return data;
}

async function getJson(url) {
  const res = await fetch(url, { credentials: 'include' });
  const ct = res.headers.get('content-type') || '';
  if (!ct.includes('json')) {
    const err = new Error('api_unavailable');
    err.status = 'api_unavailable';
    throw err;
  }
  return jsonOrThrow(res);
}

export function newExpenseId() {
  return `exp_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`;
}

export async function fetchExpenses({ tripId = TRIP_DEFAULT } = {}) {
  return getJson(`/api/expenses?trip_id=${tripId}`);
}

export async function createExpense({ tripId = TRIP_DEFAULT, ...fields }) {
  const res = await fetch('/api/expenses', {
    method: 'POST',
    credentials: 'include',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ trip_id: tripId, ...fields }),
  });
  return jsonOrThrow(res);
}

export async function updateExpense({ tripId = TRIP_DEFAULT, expenseId, ...patch }) {
  const res = await fetch('/api/expenses', {
    method: 'PUT',
    credentials: 'include',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ trip_id: tripId, expense_id: expenseId, ...patch }),
  });
  return jsonOrThrow(res);
}

export async function deleteExpense({ tripId = TRIP_DEFAULT, expenseId }) {
  const res = await fetch('/api/expenses', {
    method: 'DELETE',
    credentials: 'include',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ trip_id: tripId, expense_id: expenseId }),
  });
  return jsonOrThrow(res);
}

async function signReceiptUpload({ tripId, expenseId }) {
  const res = await fetch('/api/expenses/sign-upload', {
    method: 'POST',
    credentials: 'include',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ trip_id: tripId, expense_id: expenseId }),
  });
  return jsonOrThrow(res);
}

export async function uploadReceipt({ tripId = TRIP_DEFAULT, expenseId, file, onProgress }) {
  const signed = await signReceiptUpload({ tripId, expenseId });
  const uploaded = await uploadToCloudinary({ file, signed, onProgress });
  return { secure_url: uploaded.secure_url, image_metadata: uploaded.image_metadata || null };
}

export async function extractReceipt({ tripId = TRIP_DEFAULT, photoUrl }) {
  const res = await fetch('/api/expenses/extract-receipt', {
    method: 'POST',
    credentials: 'include',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ trip_id: tripId, photo_url: photoUrl }),
  });
  return jsonOrThrow(res);
}
